'use client'

import { IconLink, IconMoreHorizontal } from '@macarongfactory/web-icons'
import { FloatingActionButton, Text, Image } from '@macarongfactory/web-ui'
import { useRef } from 'react'

import { Space } from '@/shared/ui'

import {
  CAR_STORY_SHARE_CHANNEL,
  KAKAO_SHARE_BUTTON_IMAGE_URL,
} from '../lib/shareMeta'
import { carStoryTracking } from '../lib/tracking'
import useCarStoryShare, {
  type ICarStoryShareMeta,
} from '../model/useCarStoryShare'
import useTrackOnVisible from '../model/useTrackOnVisible'

interface ICarStoryContentDetailShareSectionProps {
  contentId: number
  shareMeta: ICarStoryShareMeta
}

const CarStoryContentDetailShareSection = ({
  contentId,
  shareMeta,
}: ICarStoryContentDetailShareSectionProps) => {
  const { handleKakaoShare, handleCopyLink, handleShareMore } =
    useCarStoryShare({ shareMeta })

  const handleClickKakao = () => {
    carStoryTracking.clickDetailShareButton(
      CAR_STORY_SHARE_CHANNEL.KAKAO,
      contentId
    )
    handleKakaoShare()
  }

  const handleClickLink = () => {
    carStoryTracking.clickDetailShareButton(
      CAR_STORY_SHARE_CHANNEL.LINK,
      contentId
    )
    handleCopyLink()
  }

  const handleClickMore = () => {
    carStoryTracking.clickDetailShareButton(
      CAR_STORY_SHARE_CHANNEL.MORE,
      contentId
    )
    handleShareMore()
  }

  return (
    <section
      id='car-story-share-section'
      className='bg-background-surface-default gap-dimension-x5 pt-dimension-x6 px-dimension-x5 flex flex-col items-center'
    >
      <Text
        variant='title-1-semibold'
        color='text-foreground-neutral-strong'
        className='w-full text-center'
      >
        이 콘텐츠를 공유해 보세요!
      </Text>
      <div className='bg-background-neutral-weak rounded-dimension-x4 py-dimension-x10 gap-dimension-x5 flex w-full items-center justify-center'>
        <ShareButtonWrapper
          contentId={contentId}
          channel={CAR_STORY_SHARE_CHANNEL.KAKAO}
        >
          <Image
            src={KAKAO_SHARE_BUTTON_IMAGE_URL}
            alt='카카오톡 공유'
            width={56}
            height={56}
            radius='50%'
            scaletype='crop'
            onClick={handleClickKakao}
          />
        </ShareButtonWrapper>
        <ShareButtonWrapper
          contentId={contentId}
          channel={CAR_STORY_SHARE_CHANNEL.LINK}
        >
          <FloatingActionButton
            variant='neutral'
            size='xlarge'
            icon={<IconLink />}
            clickable={handleClickLink}
            className='!shadow-none'
          />
        </ShareButtonWrapper>
        <ShareButtonWrapper
          contentId={contentId}
          channel={CAR_STORY_SHARE_CHANNEL.MORE}
        >
          <FloatingActionButton
            variant='white'
            size='xlarge'
            icon={<IconMoreHorizontal />}
            clickable={handleClickMore}
            className='!shadow-none'
          />
        </ShareButtonWrapper>
      </div>
      <Space h={154} />
    </section>
  )
}

const ShareButtonWrapper = ({
  contentId,
  channel,
  children,
}: {
  contentId: number
  channel: string
  children: React.ReactNode
}) => {
  const ref = useRef<HTMLDivElement | null>(null)
  useTrackOnVisible({
    ref,
    tracker: () => carStoryTracking.showDetailShareButton(channel, contentId),
  })

  return <div ref={ref}>{children}</div>
}

export default CarStoryContentDetailShareSection
